import {
  Game,
  GameState,
  GameType,
  PlayerPublic,
  postGameByGameIdEndMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  toast,
} from "@win-reality/react-ui";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  setCurrentCatcher,
  setCurrentPitcher,
} from "../../../store/slices/game";
import {
  getHomeAwayLineups,
  mapLineupEntryToIPLayer,
} from "../../../utils/api-helpers";
import { handleErrorNotification } from "../../../utils/handleErrorNotification";
import { useUpdateGameState } from "../../../utils/useUpdateGameState";
import { BattingPracticeModeInfo } from "./BattingPracticeModeInfo";
import { BullpenModeInfo } from "./BullpenModeInfo";
import { GameModeInfo } from "./GameModeInfo";
import { endGameEffects, resumeGameEffects } from "./helpers";

type ActiveGameModalProps = {
  opened: boolean;
  onClose: () => void;
  gameState: GameState;
  game: Game;
  awayTeamPlayers: PlayerPublic[];
  homeTeamPlayers: PlayerPublic[];
  selectedMode?: string;
};

const label: Record<GameType, string> = {
  game: "Game",
  bullpen: "Bullpen",
  batting_practice: "BP",
};

const selectedModeLabel: Record<string, string> = {
  game: "Game",
  bullpen: "Bullpen",
  "batting-practice": "BP",
};

export const ActiveGameModal = ({
  opened,
  onClose,
  game,
  gameState,
  awayTeamPlayers,
  homeTeamPlayers,
  selectedMode,
}: ActiveGameModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const updateGameState = useUpdateGameState();
  const [isResumingGame, setIsResumingGame] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { type: gameType } = game;

  const gameLabel = label[gameType as GameType];
  const newModeLabel = selectedMode
    ? (selectedModeLabel[selectedMode] ?? gameLabel)
    : gameLabel;

  // Map selectedMode (URL route format) to the target mode for navigation
  const targetMode: string =
    selectedMode ??
    (gameType === "batting_practice"
      ? "batting-practice"
      : (gameType ?? "game"));

  const { mutationFn } = postGameByGameIdEndMutation();
  const { mutate: endGameMutation, isPending: isEndingGame } = useMutation({
    mutationFn,
    onSuccess: () => {
      endGameEffects(dispatch);
      onClose();
      toast(`The ${gameLabel.toLowerCase()} has been ended!`, {
        variant: "success",
      });

      // Navigate to the selected mode — mode view handles its own setup
      navigate(targetMode, { state: { showSetup: true } });
    },
    onError: (error: Error) =>
      handleErrorNotification(
        "An error occurred while trying to end the game",
        error,
      ),
  });

  const handleStartNewGame = () => {
    setShowConfirmation(true);
  };

  const handleConfirmStartNew = () => {
    setShowConfirmation(false);
    endGameMutation({
      body: {},
      path: {
        // @ts-expect-error gameUuid is not correctly typed in the API spec.
        game_id: game.gameUuid,
      },
    });
  };

  const handleResumeGame = () => {
    setIsResumingGame(true);
    resumeGameEffects(dispatch, { game, awayTeamPlayers, homeTeamPlayers });
    updateGameState(gameState, game);

    // Preserve both teams' pitchers/catchers from the lineups
    const { home: homeLineup, away: awayLineup } = getHomeAwayLineups(game);

    if (awayLineup.length > 0) {
      const awayPitcherEntry = awayLineup.find(
        (entry) => entry.position === "pitcher",
      );
      const awayCatcherEntry = awayLineup.find(
        (entry) => entry.position === "catcher",
      );

      if (awayPitcherEntry) {
        dispatch(
          setCurrentPitcher({
            team: "away",
            player: mapLineupEntryToIPLayer(awayPitcherEntry),
          }),
        );
      }
      if (awayCatcherEntry) {
        dispatch(
          setCurrentCatcher({
            team: "away",
            player: mapLineupEntryToIPLayer(awayCatcherEntry),
          }),
        );
      }
    }

    if (homeLineup.length > 0) {
      const homePitcherEntry = homeLineup.find(
        (entry) => entry.position === "pitcher",
      );
      const homeCatcherEntry = homeLineup.find(
        (entry) => entry.position === "catcher",
      );

      if (homePitcherEntry) {
        dispatch(
          setCurrentPitcher({
            team: "home",
            player: mapLineupEntryToIPLayer(homePitcherEntry),
          }),
        );
      }
      if (homeCatcherEntry) {
        dispatch(
          setCurrentCatcher({
            team: "home",
            player: mapLineupEntryToIPLayer(homeCatcherEntry),
          }),
        );
      }
    }

    setIsResumingGame(false);
    onClose();

    // Navigate to the appropriate view based on game type
    const routeMap = {
      game: "game",
      bullpen: "bullpen",
      batting_practice: "batting-practice",
    };

    navigate(routeMap[gameType as GameType]);
  };

  const gameTypeComponents: Record<GameType, React.ReactNode> = {
    game: <GameModeInfo game={game} gameState={gameState} />,
    bullpen: <BullpenModeInfo game={game} />,
    batting_practice: <BattingPracticeModeInfo game={game} />,
  };

  return (
    <>
      {/* Main Active Game Modal */}
      <Dialog
        open={opened}
        onOpenChange={onClose}
        defaultCancelButton={false}
        size="lg"
      >
        {({ showDivider, setScrollContainer }) => (
          <>
            <DialogScrollableContent setScrollContainer={setScrollContainer}>
              <DialogHeader>
                <DialogTitle>{`Active ${gameLabel}`}</DialogTitle>
              </DialogHeader>

              {gameTypeComponents[gameType as GameType]}

              <p className="text-text-light mt-2">
                There is an active {gameLabel}. What would you like to do?
              </p>
            </DialogScrollableContent>

            <DialogFooter showDivider={showDivider}>
              <div className="flex w-full justify-between">
                <Button
                  variant="ghost"
                  type="button"
                  className="underline"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <div className="flex gap-3">
                  <Button
                    variant="secondary-flat-light"
                    onClick={handleStartNewGame}
                    isDisabled={isEndingGame || isResumingGame}
                  >
                    {`Start New ${newModeLabel}`}
                  </Button>
                  <Button
                    variant="secondary-flat-light"
                    onClick={handleResumeGame}
                    isDisabled={isEndingGame || isResumingGame}
                    isLoading={isResumingGame}
                  >
                    {`Resume ${gameLabel}`}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </>
        )}
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onOpenChange={setShowConfirmation}
        defaultCancelButton={false}
        size="md"
      >
        {({ showDivider, setScrollContainer }) => (
          <>
            <DialogScrollableContent setScrollContainer={setScrollContainer}>
              <DialogHeader>
                <DialogTitle>{`End Current ${gameLabel}?`}</DialogTitle>
              </DialogHeader>

              <p className="text-text-light">
                Are you sure you want to end the current{" "}
                {gameLabel.toLowerCase()} and start a new one? This action
                cannot be undone.
              </p>
            </DialogScrollableContent>

            <DialogFooter showDivider={showDivider}>
              <div className="flex w-full justify-between">
                <Button
                  variant="ghost"
                  type="button"
                  className="underline"
                  onClick={() => setShowConfirmation(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary-flat-light"
                  onClick={handleConfirmStartNew}
                  isDisabled={isEndingGame}
                  isLoading={isEndingGame}
                >
                  {`End & Start New ${newModeLabel}`}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </>
  );
};
